import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { SessionStorageGateway } from '@core/services/session-storage.gateway'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'

const STORAGE_KEY = 'admin-mode'

/**
 * Whether the signed-in admin is currently *acting* as one. A capability
 * (`userModel.can('team.manage')`, ...) says an account MAY see admin
 * surface; this says whether it currently does — the split
 * AdminModeTogglePresenter's `showToggle`/`enabled` mirror.
 *
 * Session-scoped, not local: it should not follow the account into a new tab
 * or survive the browser closing, the same reasoning as
 * `SessionStorageGateway` itself. `UserScopedState` so a second login as a
 * non-admin on the same browser can never inherit a stale "on".
 *
 * Always resolves to something rather than starting `null` — same as
 * `ThemeRepository`: this is a preference/mode the app owns from its first
 * frame, not server data with a "not yet loaded" phase.
 */
@injectable()
export class AdminModeRepository implements UserScopedState {
  adminModePm: boolean = false

  @inject(Types.ISessionStorageGateway)
  private sessionStorageGateway!: SessionStorageGateway

  constructor() {
    makeObservable(this, { adminModePm: observable })
  }

  /**
   * Property-injected deps aren't populated during construction, so this
   * can't run there — AdminModeTogglePresenter.initialize() calls it at
   * bootstrap (same shape as ThemeRepository.restore()).
   */
  restore = action(() => {
    this.adminModePm = this.sessionStorageGateway.getValue(STORAGE_KEY) === true
  })

  setAdminMode = action((enabled: boolean) => {
    this.adminModePm = enabled
    this.sessionStorageGateway.setValue(STORAGE_KEY, enabled)
  })

  resetUserScopedState = action(() => {
    this.adminModePm = false
    this.sessionStorageGateway.remove(STORAGE_KEY)
  })
}
